// frontend/src/pages/VerifyEmail.tsx
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { verifyEmail, resendVerification } from "../services/auth";
import Spinner from "../components/ui/Spinner";

type Status = "pending" | "ok" | "error";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
    const token = sp.get("token") || "";
  const emailFromQuery = sp.get("email") || "";
  const [status, setStatus] = useState<Status>("pending");
  const [message, setMessage] = useState<string>(t("common.pending"));
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  

  useEffect(() => {
    (async () => {
      if (!token) {
        setStatus("error");
        setMessage(t("auth.verify.invalid_link"));
        return;
      }
      try {
        await verifyEmail(token);
        setStatus("ok");
        setMessage(t("auth.verify.success"));
      } catch (e: any) {
        const detail = e?.response?.data?.detail || e?.message || t("auth.verify.failed");
        setStatus("error");
        setMessage(String(detail));
      }
    })();
  }, [token]);

  const onResend = async () => {
    const email =
      emailFromQuery ||
      window.prompt(t("auth.verify.prompt_email")) ||
      "";
    if (!email) return;
    try {
      setResending(true);
      await resendVerification(email);
      setResendDone(true);
      setMessage(t("auth.verify.resend_success"));
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || t("auth.verify.resend_failed");
      setMessage(String(detail));
    } finally {
      setResending(false);
    }
  };

  const msgColor =
    status === "error" ? "text-elani-orange" : status === "pending" ? "text-gray-700" : "text-gray-900";

  return (
    <div className="min-h-full bg-gray-50 px-4 pt-12 md:pt-16 lg:pt-0 lg:py-8 lg:flex-1 lg:grid lg:place-items-center">
      <div className="mx-auto w-full max-w-md bg-white shadow rounded px-6 py-6 space-y-4">
        {/* Title (no spinner here) */}
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">{t("auth.verify.title")}</h1>
        </div>

        {/* Spinner placed directly before message text */}
        <div className="flex items-center gap-2">
          {status === "pending" && <Spinner size="xs" aria-label={t("auth.verify.a11y_verifying")} />}
          <p className={msgColor}>{message}</p>
        </div>

        {/* Plain hint text (no box) when error */}
        {status === "error" && (
          <p className="text-sm text-gray-700">
            {t("auth.verify.hint")}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded px-4 py-2 bg-elani-blue text-white hover:opacity-90"
          >
            {t("auth.verify.go_to_login")}
          </Link>

          {status === "error" && (
            <button
              type="button"
              onClick={onResend}
              disabled={resending || resendDone}
              className="inline-flex items-center justify-center rounded px-4 py-2 bg-elani-orange hover:bg-elani-orange-dark text-white disabled:opacity-60"
            >
              {resending ? (
                <>
                  <Spinner size="xs" className="mr-2" aria-label={t("auth.verify.a11y_verifying_label")}  />
                  {t("auth.verify.resending")}
                </>
              ) : resendDone ? (
                t("auth.verify.resent")
              ) : (
                t("auth.verify.resend")
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}